import React, { useState, useEffect } from 'react';
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';
import ExcelJS from 'exceljs';

import css from './DishesComponent.module.css';
import DishesListComponent from "../DishesListComponent/DishesListComponent ";
import { DishCreateModal, ImportDishesXLSModal } from "../dishesSectionModals";
import { ButtonCreate } from "../../../ui/ButtonCreate/ButtonCreate";
import { dishesActions } from "../../../store/slices/dishes-slices";
import { paginationActions } from "../../../store/slices/pagination-slices";
import { Button } from 'antd';
import { MdAdd, MdDownload, MdUpload } from 'react-icons/md';
import { dishesService } from '../../../services/dishes-services';


const DishesComponent = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isImportOpen, setIsImportOpen] = useState(false);
    const dispatch = useDispatch();
    const { handleSubmit, register, setValue } = useForm();
    const { filterParams } = useSelector(store => store.dishes);
    const [loadingXlsAllDishes, setLoadingXlsAllDishes] = useState(false);
    const { t, i18n } = useTranslation();

    // Clear search when component mounts
    useEffect(() => {
        dispatch(dishesActions.clearFilters());
        setValue('dishesSearch', ''); // Clear the input field
    }, [dispatch, setValue]);

    const submit = (data) => {
        // Reset pagination to page 1 when search changes
        dispatch(paginationActions.changePage(1));

        dispatch(dishesActions.setFilterParams({ word: data.dishesSearch.trim() || '' }));
        dispatch(dishesActions.getAllDishesPaginated({
            page: 1, // Always start from page 1 for new search
            ...filterParams,
            word: data.dishesSearch.trim() || ''
        }));
    }

    const handleClick = () => {
        setIsOpen(true);
    }

    const handleClickXlsAllDishes = async () => {
        try {
            setLoadingXlsAllDishes(true);
            const dishesData = await dishesService.exportAllDishesWithIngredients();
            
            if (!dishesData || dishesData.length === 0) {
                alert(t('No dishes data available'));
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.creator = 'GWT ERP';
            workbook.lastModifiedBy = 'GWT ERP';
            workbook.created = new Date();
            workbook.modified = new Date();

            const worksheet = workbook.addWorksheet('Dishes with Ingredients');

            // Set up columns
            worksheet.columns = [
                { key: 'dishName', width: 50 },
                { key: 'ingredientType', width: 20 },
                { key: 'ingredientName', width: 40 },
                { key: 'weight', width: 15 },
            ];

            // Create title row
            const titleRow = worksheet.addRow(['All Dishes with Ingredients']);
            titleRow.font = { bold: true, size: 14 };
            worksheet.mergeCells('A1:D1');
            worksheet.getCell('A1').alignment = { horizontal: 'center' };
            titleRow.eachCell(cell => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: 'E6F4FF' }
                };
            });

            // Create header row
            const headerRow = worksheet.addRow([
                'Dish Name',
                'Ingredient Type',
                'Ingredient Name',
                'Weight (g)'
            ]);
            headerRow.font = { bold: true, color: { argb: 'FFFFFF' } };
            headerRow.eachCell(cell => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '4472C4' }
                };
                cell.alignment = { horizontal: 'center' };
            });

            // Get current language
            const currentLanguage = i18n.language;

            // Add data rows
            dishesData.forEach((dish, index) => {
                const dishName = dish.name?.[currentLanguage] || dish.name?.en || dish.name?.ru || 'Unknown Dish';
                
                // Add dish header row
                const dishHeaderRow = worksheet.addRow({
                    dishName: dishName,
                    ingredientType: '',
                    ingredientName: '',
                    weight: ''
                });
                dishHeaderRow.font = { bold: true, size: 11 };
                dishHeaderRow.eachCell(cell => {
                    cell.fill = {
                        type: 'pattern',
                        pattern: 'solid',
                        fgColor: { argb: 'FFE5CC' }
                    };
                });

                // Add products
                if (dish.products && dish.products.length > 0) {
                    dish.products.forEach(product => {
                        const productName = product.name?.[currentLanguage] || product.name?.en || product.name?.ru || 'Unknown Product';
                        const row = worksheet.addRow({
                            dishName: '',
                            ingredientType: 'Product',
                            ingredientName: productName,
                            weight: product.weight || '0'
                        });
                        row.eachCell(cell => {
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'FFFFFF' }
                            };
                        });
                    });
                }

                // Add semi-finished products
                if (dish.semiFinisheds && dish.semiFinisheds.length > 0) {
                    dish.semiFinisheds.forEach(semiFinished => {
                        const semiFinishedName = semiFinished.name?.[currentLanguage] || semiFinished.name?.en || semiFinished.name?.ru || 'Unknown Semi-Finished';
                        const row = worksheet.addRow({
                            dishName: '',
                            ingredientType: 'Semi-Finished',
                            ingredientName: semiFinishedName,
                            weight: semiFinished.weight || '0'
                        });
                        row.eachCell(cell => {
                            cell.fill = {
                                type: 'pattern',
                                pattern: 'solid',
                                fgColor: { argb: 'F0F0F0' }
                            };
                        });
                    });
                }

                // Add empty row between dishes for better readability (except after last dish)
                if (index < dishesData.length - 1) {
                    worksheet.addRow({});
                }
            });

            // Add borders to all cells
            worksheet.eachRow((row) => {
                row.eachCell((cell) => {
                    cell.border = {
                        top: { style: 'thin' },
                        left: { style: 'thin' },
                        bottom: { style: 'thin' },
                        right: { style: 'thin' }
                    };
                });
            });

            // Generate and download the Excel file
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `all_dishes_with_ingredients_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            console.error("Error exporting all dishes:", error);
            alert(t('Error exporting dishes. Please try again.'));
        } finally {
            setLoadingXlsAllDishes(false);
        }
    }

    return (
        <section className={css.wrap}>

            <section className={css.menuBlock}>
                <div className={css.menuBlockButtons}>
                    <Button onClick={handleClick} type="primary" icon={<MdAdd />}>Create Dish</Button>
                    {/* export xls all dishes*/}
                    <Button onClick={handleClickXlsAllDishes} type='dashed' icon={<MdDownload />} loading={loadingXlsAllDishes}>Export XLS All Dishes</Button>
                    <Button onClick={() => setIsImportOpen(true)} type='dashed' icon={<MdUpload />}>Import XLS</Button>
                </div>

                <form onChange={handleSubmit(submit)} className={css.form}>
                    <input type="text" {...register("dishesSearch")} placeholder={'Search'} />
                </form>
            </section>

            <section className={css.listBlock}>
                <DishesListComponent />
            </section>

            {isOpen && <DishCreateModal setIsOpen={setIsOpen} />}
            {isImportOpen && <ImportDishesXLSModal setIsOpen={setIsImportOpen} />}
        </section>
    );
};

export default React.memo(DishesComponent);